import { useEffect, useState, useMemo } from "react";
import { toast } from "sonner";
import { Loader2, Trash2, Download, RefreshCw, Mail, Phone, Inbox, Send, Archive, CheckCheck, X, ChevronLeft, Settings2 } from "lucide-react";
import { listSubmissions, deleteSubmission, updateSubmission, replySubmission, bulkSubmissions, listReplyTemplates, createReplyTemplate, updateReplyTemplate, deleteReplyTemplate, reclassifySubmission } from "@/lib/api";

const TYPE_LABELS = { projet: "Projet", formation: "Inscription formation", festival: "Festival", casting: "Casting", devis: "Devis", inscription: "Inscription", contact: "Contact", demande_info: "Demande d'information" };
const TYPE_STYLES = { projet: "text-electric border-electric/40", formation: "text-emerald-400 border-emerald-400/40", festival: "text-amber-400 border-amber-400/40", contact: "text-sky-400 border-sky-400/40", casting: "text-fuchsia-400 border-fuchsia-400/40", inscription: "text-emerald-400 border-emerald-400/40", devis: "text-white border-white/30", demande_info: "text-sky-300 border-sky-300/40" };
const STATUS_OPTS = [["nouveau", "Nouvelle / Non lu"], ["lu", "Lu"], ["en_attente", "En attente"], ["acceptee", "Acceptée"], ["refusee", "Refusée"], ["a_traiter", "À traiter"], ["echec_envoi", "Échec d'envoi"], ["traite", "Traité"], ["archive", "Archivé"]];
const STATUS_LABEL = Object.fromEntries(STATUS_OPTS);
const isUnread = (r) => ["nouveau", "new"].includes(r.status);

const FILTERS = [["all", "Toutes"], ["unread", "Non lus"], ["a_traiter", "À traiter"], ["traite", "Traité"], ["archive", "Archivés"]];

export const AdminSubmissions = () => {
  const [rows, setRows] = useState(null);
  const [filter, setFilter] = useState("all");
  const [kindFilter, setKindFilter] = useState(""); // "" | "info" | "inscription"
  const [typeFilter, setTypeFilter] = useState("");
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [active, setActive] = useState(null);
  const [reply, setReply] = useState("");
  const [replySubject, setReplySubject] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [showTemplates, setShowTemplates] = useState(false);
  const [editTemplate, setEditTemplate] = useState(null); // null | "new" | {…}

  const load = async () => {
    setLoading(true);
    try {
      const [subs, tpl] = await Promise.all([listSubmissions(), listReplyTemplates()]);
      setRows(subs);
      setTemplates(tpl.filter((t) => t.enabled !== false));
    } catch { toast.error("Impossible de charger la boîte de réception."); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    let r = rows || [];
    if (filter === "unread") r = r.filter(isUnread);
    else if (filter !== "all") r = r.filter((x) => x.status === filter);
    else r = r.filter((x) => x.status !== "archive");
    if (kindFilter === "info") r = r.filter((x) => x.type === "demande_info");
    else if (kindFilter === "inscription") r = r.filter((x) => x.type === "formation" || x.type === "inscription");
    if (typeFilter) r = r.filter((x) => x.type === typeFilter);
    if (query.trim()) {
      const q = query.toLowerCase();
      r = r.filter((x) => `${x.name} ${x.email} ${x.message} ${x.subject || ""} ${x.refTitle || ""}`.toLowerCase().includes(q));
    }
    return r;
  }, [rows, filter, kindFilter, typeFilter, query]);

  const unread = (rows || []).filter(isUnread).length;
  const activeRow = (rows || []).find((r) => r.id === active);

  const open = async (r) => {
    setActive(r.id); setReply(""); setReplySubject(`Re: ${r.refTitle || "Votre demande"}`);
    if (isUnread(r)) {
      setRows((rs) => rs.map((x) => x.id === r.id ? { ...x, status: "lu" } : x));
      try { await updateSubmission(r.id, { status: "lu" }); } catch { /* noop */ }
    }
  };

  const setStatus = async (id, status) => {
    setRows((rs) => rs.map((x) => x.id === id ? { ...x, status } : x));
    try { await updateSubmission(id, { status }); toast.success("Statut mis à jour."); } catch { toast.error("Échec."); }
  };

  // ---- Modèles de réponses ----
  const fillTemplate = (text, r) => {
    const s = r.sessionLabel || "";
    const map = {
      "{prenom}": (r.name || "").split(" ")[0] || "Madame, Monsieur",
      "{nom}": r.name || "",
      "{formation}": r.refTitle || "",
      "{date}": s.split("·")[0]?.trim() || "",
      "{horaires}": s.split("·")[1]?.trim() || "",
      "{lieu}": s.split("·")[2]?.trim() || "",
    };
    return text.replace(/\{(prenom|nom|formation|date|horaires|lieu)\}/g, (m) => map[m] ?? "");
  };

  const applyTemplate = (tplId) => {
    const t = templates.find((x) => x.id === tplId);
    if (!t || !activeRow) return;
    setReplySubject(fillTemplate(t.subject || "", activeRow));
    setReply(fillTemplate(t.body || "", activeRow));
    toast.success(`Modèle « ${t.label} » prérempli — vérifiez puis envoyez.`);
  };

  const saveTemplate = async () => {
    if (!editTemplate.label.trim() || !editTemplate.body.trim()) { toast.error("Nom et corps requis."); return; }
    try {
      let saved;
      if (editTemplate.id) saved = await updateReplyTemplate(editTemplate.id, editTemplate);
      else saved = await createReplyTemplate(editTemplate);
      setTemplates((ts) => {
        const others = ts.filter((x) => x.id !== saved.id);
        return [...others, saved].sort((a, b) => (a.order || 0) - (b.order || 0));
      });
      setEditTemplate(null);
      toast.success("Modèle enregistré.");
    } catch { toast.error("Échec de l'enregistrement."); }
  };

  const removeTemplate = async (id) => {
    if (!window.confirm("Supprimer ce modèle ?")) return;
    try { await deleteReplyTemplate(id); setTemplates((ts) => ts.filter((x) => x.id !== id)); toast.success("Modèle supprimé."); }
    catch { toast.error("Échec."); }
  };

  const sendReply = async () => {
    if (!reply.trim()) { toast.error("Écrivez une réponse."); return; }
    setSending(true);
    try {
      const res = await replySubmission(active, reply, replySubject);
      const ok = res.status === "success";
      setRows((rs) => rs.map((x) => x.id === active ? { ...x, status: ok ? "traite" : "echec_envoi", replies: [...(x.replies || []), res.reply] } : x));
      setReply("");
      toast[ok ? "success" : "warning"](ok ? "Réponse envoyée par e-mail." : "E-mail NON envoyé — réponse enregistrée. Réessayez.");
    } catch { toast.error("Échec de l'envoi."); }
    finally { setSending(false); }
  };

  const retryReply = async (subId, rep) => {
    setSending(true);
    try {
      const res = await replySubmission(subId, rep.message, rep.subject || "", rep.id);
      const ok = res.status === "success";
      setRows((rs) => rs.map((x) => x.id === subId ? { ...x, status: ok ? "traite" : "echec_envoi", replies: (x.replies || []).map((r) => r.id === rep.id ? res.reply : r) } : x));
      toast[ok ? "success" : "warning"](ok ? "Réponse renvoyée avec succès." : "Nouvel échec d'envoi.");
    } catch { toast.error("Échec de l'envoi."); }
    finally { setSending(false); }
  };

  const removeOne = async (id) => {
    if (!window.confirm("Supprimer cette demande ?")) return;
    try { await deleteSubmission(id); setRows((rs) => rs.filter((x) => x.id !== id)); if (active === id) setActive(null); toast.success("Supprimé."); }
    catch { toast.error("Échec."); }
  };

  const toggleSel = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const allSel = filtered.length > 0 && filtered.every((r) => selected.has(r.id));
  const toggleAll = () => setSelected(allSel ? new Set() : new Set(filtered.map((r) => r.id)));

  const bulk = async (action) => {
    const ids = [...selected];
    if (!ids.length) return;
    if (action === "delete" && !window.confirm(`Supprimer ${ids.length} demande(s) ?`)) return;
    try {
      await bulkSubmissions(ids, action);
      if (action === "delete") setRows((rs) => rs.filter((x) => !selected.has(x.id)));
      else setRows((rs) => rs.map((x) => selected.has(x.id) ? { ...x, status: action === "archive" ? "archive" : "lu" } : x));
      setSelected(new Set());
      toast.success("Action effectuée.");
    } catch { toast.error("Échec de l'action groupée."); }
  };

  // Reclasse une demande d'information en candidature (ou inverse). Aucune place déduite automatiquement.
  const reclassify = async (id, target) => {
    try {
      await reclassifySubmission(id, target);
      setRows((rs) => rs.map((x) => x.id === id ? { ...x, type: target, status: target === "demande_info" ? "nouveau" : "en_attente" } : x));
      toast.success(target === "demande_info" ? "Reclassé en demande d'information." : "Reclassé en demande d'inscription (en attente de validation).");
    } catch { toast.error("Échec du reclassement."); }
  };

  const exportCsv = () => {
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const header = ["Date", "Type", "Statut", "Nom", "Email", "Téléphone", "Rôle", "Concerne", "Session", "Message"];
    const lines = filtered.map((r) => [new Date(r.created_at).toLocaleString("fr-FR"), TYPE_LABELS[r.type] || r.type, STATUS_LABEL[r.status] || r.status, r.name, r.email, r.phone, r.role, r.refTitle, r.sessionLabel, r.message].map(esc).join(","));
    const blob = new Blob(["\uFEFF" + [header.join(","), ...lines].join("\n")], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = `boite-reception-karams.csv`; a.click();
    toast.success("Export CSV téléchargé.");
  };

  return (
    <div className="space-y-4" data-testid="admin-submissions">
      <div className="rounded-lg border border-electric/20 bg-[#0a0a0a] p-4 text-sm text-secondary2 flex items-center gap-3">
        <Inbox size={18} className="text-electric" />
        <span>Boîte de réception centralisée : tous les formulaires (contact, inscriptions, candidatures) arrivent ici. Ouvrez un message pour y <strong className="text-white">répondre par e-mail</strong>. {unread > 0 && <span className="text-electric font-semibold">{unread} non lu{unread > 1 ? "s" : ""}.</span>}</span>
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-1.5">
          {FILTERS.map(([k, label]) => (
            <button key={k} data-testid={`inbox-filter-${k}`} onClick={() => setFilter(k)}
              className={`px-3.5 py-1.5 rounded-full text-xs transition-colors ${filter === k ? "bg-electric text-black font-semibold" : "border border-white/10 text-secondary2 hover:text-white"}`}>
              {label}{k === "unread" && unread ? ` (${unread})` : ""}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <select value={kindFilter} onChange={(e) => setKindFilter(e.target.value)} data-testid="inbox-kind-filter" className="bg-[#0e0e0e] border border-white/10 rounded-lg px-3 py-2 text-white text-xs focus:border-electric outline-none">
            <option value="">Info + Inscriptions</option>
            <option value="info">Demandes d'information</option>
            <option value="inscription">Demandes d'inscription</option>
          </select>
          <button onClick={load} data-testid="inbox-refresh" className="inline-flex items-center gap-1.5 text-xs text-white/60 hover:text-electric"><RefreshCw size={14} className={loading ? "animate-spin" : ""} /></button>
          <button onClick={exportCsv} disabled={!filtered.length} data-testid="inbox-export" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full border border-white/10 text-white text-xs hover:border-electric disabled:opacity-40"><Download size={13} /> CSV</button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Rechercher un message…" data-testid="inbox-search" className="flex-1 min-w-[180px] bg-[#0e0e0e] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:border-electric outline-none" />
        <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} data-testid="inbox-type-filter" className="bg-[#0e0e0e] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:border-electric outline-none">
          <option value="">Tous les types</option>
          {Object.entries(TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
      </div>

      {/* Bulk bar */}
      {selected.size > 0 && (
        <div className="flex items-center gap-3 rounded-lg border border-electric/30 bg-electric/5 px-4 py-2.5" data-testid="inbox-bulk-bar">
          <span className="text-sm text-white">{selected.size} sélectionné(s)</span>
          <button onClick={() => bulk("read")} className="inline-flex items-center gap-1.5 text-xs text-secondary2 hover:text-electric"><CheckCheck size={14} /> Marquer lu</button>
          <button onClick={() => bulk("archive")} className="inline-flex items-center gap-1.5 text-xs text-secondary2 hover:text-electric"><Archive size={14} /> Archiver</button>
          <button onClick={() => bulk("delete")} className="inline-flex items-center gap-1.5 text-xs text-red-400/80 hover:text-red-400"><Trash2 size={14} /> Supprimer</button>
        </div>
      )}

      {rows === null ? (
        <div className="py-20 flex justify-center"><Loader2 className="animate-spin text-electric" /></div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)] gap-4">
          {/* List */}
          <div className="space-y-2">
            {filtered.length > 0 && (
              <label className="flex items-center gap-2 text-xs text-secondary2 px-1">
                <input type="checkbox" checked={allSel} onChange={toggleAll} data-testid="inbox-select-all" /> Tout sélectionner
              </label>
            )}
            {filtered.length === 0 ? (
              <div className="py-16 text-center text-secondary2"><Inbox size={30} className="mx-auto mb-3 opacity-40" /> Aucun message.</div>
            ) : filtered.map((r) => (
              <div key={r.id} data-testid={`inbox-row-${r.id}`} onClick={() => open(r)}
                className={`rounded-lg border p-3.5 cursor-pointer transition-colors ${active === r.id ? "border-electric bg-electric/5" : isUnread(r) ? "border-white/15 bg-white/[0.03]" : "border-white/10 bg-[#0a0a0a] hover:border-white/25"}`}>
                <div className="flex items-start gap-3">
                  <input type="checkbox" checked={selected.has(r.id)} onChange={(e) => { e.stopPropagation(); toggleSel(r.id); }} onClick={(e) => e.stopPropagation()} className="mt-1" data-testid={`inbox-check-${r.id}`} />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      {isUnread(r) && <span className="h-2 w-2 rounded-full bg-electric shrink-0" />}
                      <span className={`text-[10px] tracking-[0.12em] uppercase border rounded-full px-2 py-0.5 ${TYPE_STYLES[r.type] || "text-white border-white/20"}`}>{TYPE_LABELS[r.type] || r.type}</span>
                      {r.refTitle && <span className="text-xs text-white/60 truncate">{r.refTitle}</span>}
                    </div>
                    <div className={`truncate ${isUnread(r) ? "text-white font-medium" : "text-white/80"}`}>{r.name}</div>
                    <div className="text-xs text-secondary2 truncate">{r.subject || r.message}</div>
                  </div>
                  <span className="text-[10px] text-secondary2 whitespace-nowrap shrink-0">{new Date(r.created_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "short" })}</span>
                </div>
              </div>
            ))}
          </div>

          {/* Detail */}
          <div className={`${active ? "fixed inset-0 z-[9990] bg-[#050505] p-4 overflow-y-auto lg:static lg:z-auto lg:bg-transparent lg:p-0" : "hidden lg:block"}`}>
            {!activeRow ? (
              <div className="hidden lg:flex h-full min-h-[300px] items-center justify-center rounded-lg border border-white/10 text-secondary2 text-sm">Sélectionnez un message pour le lire et y répondre.</div>
            ) : (
              <div className="rounded-lg border border-white/10 bg-[#0a0a0a] p-5" data-testid="inbox-detail">
                <div className="flex items-start justify-between gap-3 mb-4">
                  <div>
                    <button onClick={() => setActive(null)} className="lg:hidden inline-flex items-center gap-1 text-xs text-secondary2 mb-2"><ChevronLeft size={14} /> Retour</button>
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className={`text-[10px] tracking-[0.12em] uppercase border rounded-full px-2 py-0.5 ${TYPE_STYLES[activeRow.type] || "text-white border-white/20"}`}>{TYPE_LABELS[activeRow.type] || activeRow.type}</span>
                      {activeRow.role && <span className="text-[10px] uppercase text-secondary2 border border-white/10 rounded-full px-2 py-0.5">{activeRow.role}</span>}
                    </div>
                    <h3 className="font-serif text-2xl text-white">{activeRow.name}</h3>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1.5 text-xs text-secondary2">
                      <a href={`mailto:${activeRow.email}`} className="inline-flex items-center gap-1.5 hover:text-electric"><Mail size={12} /> {activeRow.email}</a>
                      {activeRow.phone && <a href={`tel:${activeRow.phone}`} className="inline-flex items-center gap-1.5 hover:text-electric"><Phone size={12} /> {activeRow.phone}</a>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <select value={activeRow.status} onChange={(e) => setStatus(activeRow.id, e.target.value)} data-testid="inbox-status-select" className="bg-[#0e0e0e] border border-white/10 rounded-lg px-2 py-1.5 text-white text-xs focus:border-electric outline-none">
                      {STATUS_OPTS.map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                    <button onClick={() => removeOne(activeRow.id)} className="text-red-400/70 hover:text-red-400"><Trash2 size={16} /></button>
                  </div>
                </div>

                {(activeRow.refTitle || activeRow.sessionLabel) && (
                  <div className="mb-3 text-xs text-secondary2 space-y-1">
                    {activeRow.refTitle && <div>Concerne : <span className="text-white">{activeRow.refTitle}</span></div>}
                    {activeRow.sessionLabel && <div>Session : <span className="text-white">{activeRow.sessionLabel}</span></div>}
                  </div>
                )}

                {/* Reclassement info ↔ inscription */}
                <div className="mb-3">
                  {activeRow.type === "demande_info" && (activeRow.refId || activeRow.sessionId) && (
                    <button onClick={() => reclassify(activeRow.id, "formation")} data-testid="inbox-reclassify-to-inscription"
                      className="text-[11px] uppercase tracking-[0.12em] rounded-full border border-emerald-400/40 text-emerald-300 px-3 py-1.5 hover:bg-emerald-400/10 transition-colors">
                      → Reclasser en demande d'inscription
                    </button>
                  )}
                  {(activeRow.type === "formation" || activeRow.type === "inscription") && (
                    <button onClick={() => reclassify(activeRow.id, "demande_info")} data-testid="inbox-reclassify-to-info"
                      className="text-[11px] uppercase tracking-[0.12em] rounded-full border border-sky-300/40 text-sky-300 px-3 py-1.5 hover:bg-sky-300/10 transition-colors">
                      → Reclasser en demande d'information
                    </button>
                  )}
                </div>

                <div className="text-xs text-secondary2 mb-1">{new Date(activeRow.created_at).toLocaleString("fr-FR")}</div>
                <div className="rounded-lg bg-[#0e0e0e] border border-white/10 p-4 text-sm text-white/90 whitespace-pre-line mb-5">{activeRow.message || "(Aucun message)"}</div>

                {/* Conversation */}
                {(activeRow.replies || []).length > 0 && (
                  <div className="space-y-3 mb-5" data-testid="inbox-conversation">
                    {activeRow.replies.map((rep, i) => (
                      <div key={rep.id || i} className="ml-6 rounded-lg bg-electric/5 border border-electric/20 p-3" data-testid={`inbox-reply-${i}`}>
                        <div className="flex items-center justify-between gap-2 mb-1.5">
                          <span className="text-[10px] tracking-[0.15em] uppercase text-electric">Votre réponse · {new Date(rep.created_at).toLocaleString("fr-FR")}</span>
                          {rep.sent === false
                            ? <span className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.1em] text-red-400">✗ Non envoyée
                                <button onClick={() => retryReply(activeRow.id, rep)} disabled={sending} data-testid={`inbox-reply-retry-${i}`} className="rounded-full border border-red-400/40 px-2 py-0.5 text-red-300 hover:bg-red-400 hover:text-black transition-colors disabled:opacity-60">Réessayer</button>
                              </span>
                            : <span className="text-[10px] uppercase tracking-[0.1em] text-green-400">✓ Envoyée</span>}
                        </div>
                        <div className="text-sm text-white/90 whitespace-pre-line">{rep.message}</div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Reply box — modèles prédéfinis + préremplissage auto */}
                <div className="border-t border-white/10 pt-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-[10px] tracking-[0.2em] uppercase text-secondary2">Répondre par e-mail</div>
                    <button onClick={() => { setShowTemplates((s) => !s); setEditTemplate(null); }} data-testid="inbox-manage-templates"
                      className="text-[10px] uppercase tracking-[0.12em] text-white/50 hover:text-electric">Gérer les modèles</button>
                  </div>

                  {/* Gestion des modèles (ajout / édition / suppression) */}
                  {showTemplates && (
                    <div className="mb-4 rounded-lg border border-electric/20 bg-[#0a0a0a] p-3 space-y-2" data-testid="inbox-templates-panel">
                      <div className="flex items-center justify-between">
                        <span className="text-[10px] uppercase text-electric">Modèles de réponses</span>
                        <button onClick={() => setEditTemplate({ label: "", subject: "", body: "", order: templates.length })}
                          data-testid="inbox-template-add" className="text-xs text-white/70 hover:text-electric">+ Ajouter</button>
                      </div>
                      {templates.map((t) => (
                        <div key={t.id} className="flex items-center gap-2 text-xs">
                          <span className="flex-1 text-white/80 truncate">{t.label}</span>
                          <button onClick={() => setEditTemplate({ ...t })} data-testid={`inbox-template-edit-${t.id}`} className="text-white/50 hover:text-electric">Modifier</button>
                          <button onClick={() => removeTemplate(t.id)} data-testid={`inbox-template-del-${t.id}`} className="text-red-400/70 hover:text-red-400"><Trash2 size={13} /></button>
                        </div>
                      ))}
                      {editTemplate && (
                        <div className="border-t border-white/10 pt-2 space-y-2">
                          <input value={editTemplate.label} onChange={(e) => setEditTemplate({ ...editTemplate, label: e.target.value })} placeholder="Nom du modèle" data-testid="inbox-template-label" className="w-full bg-[#0e0e0e] border border-white/10 rounded px-2 py-1.5 text-white text-xs" />
                          <input value={editTemplate.subject} onChange={(e) => setEditTemplate({ ...editTemplate, subject: e.target.value })} placeholder="Objet (peut contenir {prenom}, {formation})" data-testid="inbox-template-subject" className="w-full bg-[#0e0e0e] border border-white/10 rounded px-2 py-1.5 text-white text-xs" />
                          <textarea value={editTemplate.body} onChange={(e) => setEditTemplate({ ...editTemplate, body: e.target.value })} rows={4} placeholder="Corps… variables : {prenom} {nom} {formation} {date} {horaires} {lieu}" data-testid="inbox-template-body" className="w-full bg-[#0e0e0e] border border-white/10 rounded px-2 py-1.5 text-white text-xs" />
                          <div className="flex gap-2 justify-end">
                            <button onClick={() => setEditTemplate(null)} className="text-xs text-white/50">Annuler</button>
                            <button onClick={saveTemplate} data-testid="inbox-template-save" className="text-xs text-electric font-semibold">Enregistrer le modèle</button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Sélecteur de modèle + préremplissage */}
                  <select value="" onChange={(e) => { if (e.target.value) applyTemplate(e.target.value); }} data-testid="inbox-template-select"
                    className="w-full mb-2 bg-[#0e0e0e] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:border-electric outline-none">
                    <option value="">— Choisir une réponse prédéfinie… —</option>
                    {templates.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
                  </select>
                  <input value={replySubject} onChange={(e) => setReplySubject(e.target.value)} placeholder="Objet de l'e-mail" data-testid="inbox-reply-subject"
                    className="w-full mb-2 bg-[#0e0e0e] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:border-electric outline-none" />
                  <textarea value={reply} onChange={(e) => setReply(e.target.value)} rows={5} data-testid="inbox-reply-text" placeholder={`Répondre à ${activeRow.name}… (ou choisissez un modèle ci-dessus)`} className="w-full bg-[#0e0e0e] border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm focus:border-electric outline-none resize-y" />
                  <div className="flex justify-end mt-3">
                    <button onClick={sendReply} disabled={sending} data-testid="inbox-reply-send" className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-electric text-black text-[12px] tracking-[0.1em] uppercase font-semibold hover:bg-white transition-colors disabled:opacity-60">
                      {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Vérifier & envoyer
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
